import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, XCircle, Sparkles, CreditCard, ShieldCheck, Loader2, Users, Receipt, Minus, Plus } from "lucide-react";

/**
 * /settings/billing — customers see their plan + upgrade / cancel.
 * Also handles the Stripe Checkout return via ?session_id=... by polling
 * /api/billing/status/{session_id} until Stripe reports paid.
 */
export default function BillingSettings() {
  const { user, refreshMe } = useAuth();
  const [status, setStatus] = useState(null);
  const [plans, setPlans] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [seats, setSeats] = useState(1);
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();
  const sessionId = params.get("session_id");
  const cancelled = params.get("status") === "cancelled";

  const load = useCallback(async () => {
    try {
      const [s, p, inv] = await Promise.all([
        api.get("/billing/status"),
        api.get("/billing/plans"),
        api.get("/billing/invoices"),
      ]);
      setStatus(s.data);
      setPlans(p.data.plans);
      setInvoices(inv.data.invoices || []);
      setSeats(Math.max(1, s.data.seats_paid || 1));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  // Poll after redirect from Stripe until paid or 10 attempts.
  const pollAfterRedirect = useCallback(async (id) => {
    for (let i = 0; i < 10; i++) {
      try {
        const r = await api.get(`/billing/status/${id}`);
        if (r.data.payment_status === "paid") {
          toast.success(`Welcome to ${r.data.plan.toUpperCase()}. Payment confirmed.`);
          await refreshMe();
          await load();
          setParams({});
          return;
        }
        if (r.data.status === "expired") {
          toast.error("Payment session expired.");
          setParams({});
          return;
        }
      } catch (e) { /* keep polling */ }
      await new Promise((res) => setTimeout(res, 2000));
    }
    toast.info("Still processing. Check back in a minute — you'll get an email confirmation.");
    setParams({});
  }, [load, refreshMe, setParams]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (sessionId) pollAfterRedirect(sessionId);
    if (cancelled) toast.info("Checkout cancelled. No charges made.");
  }, [sessionId, cancelled, pollAfterRedirect]);

  const checkout = async (planSlug) => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan: planSlug,
        origin_url: window.location.origin,
        seats,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel your subscription? You'll keep access until the current period ends.")) return;
    try {
      await api.post("/billing/cancel");
      toast.success("Subscription cancelled. Access continues until the period ends.");
      await refreshMe();
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!status) {
    return <div className="p-10 text-sm text-[var(--peak-muted)]"><Loader2 className="w-4 h-4 inline animate-spin mr-2" />Loading billing…</div>;
  }

  const current = status.plan;
  const purchasable = plans.filter((p) => p.price != null && p.price > 0);

  return (
    <div className="p-6 md:p-10 max-w-6xl" data-testid="billing-page">
      <header className="mb-8">
        <div className="section-label text-[var(--peak-accent)]">SUBSCRIPTION</div>
        <h1 className="text-4xl md:text-5xl font-heading font-black tracking-tighter mt-1">BILLING</h1>
        <p className="text-[var(--peak-muted)] mt-2">Manage your plan, view invoices, or cancel anytime. Test mode.</p>
      </header>

      {/* Current plan card */}
      <CurrentPlanCard status={status} plans={plans} onCancel={cancel} />

      {/* Upgrade options — hidden if on a real paid Pro/Enterprise plan, but shown during trial. */}
      {(current !== "pro" && current !== "enterprise") || status.trial?.in_trial ? (
        <>
          <h2 className="mt-12 mb-4 font-heading font-black text-2xl tracking-tighter">
            {status.trial?.in_trial ? "Pick a plan before your trial ends" : "Change plan"}
          </h2>

          <SeatPicker seats={seats} setSeats={setSeats} />

          <div className="grid md:grid-cols-3 gap-4 mt-4">
            {purchasable.map((p) => (
              <PlanCard
                key={p.slug}
                plan={p}
                currentSlug={status.trial?.in_trial ? "trialing" : current}
                onSelect={() => checkout(p.slug)}
                busy={busy}
                seats={seats}
              />
            ))}
          </div>
          <p className="text-xs font-mono text-[var(--peak-muted)] mt-4">
            Payments processed by Stripe (test mode). Use card 4242 4242 4242 4242, any future date, any CVC.
          </p>
        </>
      ) : null}

      <InvoiceHistory invoices={invoices} />
    </div>
  );
}

function SeatPicker({ seats, setSeats }) {
  const dec = () => setSeats((s) => Math.max(1, s - 1));
  const inc = () => setSeats((s) => Math.min(50, s + 1));
  return (
    <div className="border border-[var(--peak-ink)] bg-white p-4 flex items-center justify-between" data-testid="seat-picker">
      <div className="flex items-center gap-3">
        <Users className="w-5 h-5" />
        <div>
          <div className="text-xs font-bold uppercase tracking-wider">Team seats</div>
          <div className="text-xs text-[var(--peak-muted)]">Price scales linearly. 1 owner + N invited members.</div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button data-testid="seat-decrement" onClick={dec} className="w-8 h-8 border border-[var(--peak-ink)] hover:bg-[var(--peak-ink)] hover:text-white transition-colors flex items-center justify-center">
          <Minus className="w-3.5 h-3.5" />
        </button>
        <div className="w-10 text-center font-heading font-black text-2xl tracking-tighter" data-testid="seat-count">{seats}</div>
        <button data-testid="seat-increment" onClick={inc} className="w-8 h-8 border border-[var(--peak-ink)] hover:bg-[var(--peak-ink)] hover:text-white transition-colors flex items-center justify-center">
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

function InvoiceHistory({ invoices }) {
  if (!invoices.length) return null;
  return (
    <div className="mt-16" data-testid="invoice-history">
      <div className="flex items-center gap-2 mb-4">
        <Receipt className="w-5 h-5" />
        <h2 className="font-heading font-black text-2xl tracking-tighter">Invoice history</h2>
      </div>
      <div className="border border-[var(--peak-ink)] bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[var(--peak-bg)] border-b border-[var(--peak-ink)]">
            <tr className="text-left">
              <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-[var(--peak-muted)]">Date</th>
              <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-[var(--peak-muted)]">Plan</th>
              <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-[var(--peak-muted)]">Seats</th>
              <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-[var(--peak-muted)]">Amount</th>
              <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-[var(--peak-muted)]">Status</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv) => {
              const paid = inv.payment_status === "paid";
              const initiated = inv.status === "initiated" || inv.payment_status === "pending";
              const tone = paid ? "text-[var(--peak-blue-deep)]" : initiated ? "text-[var(--peak-muted)]" : "text-[var(--peak-accent)]";
              return (
                <tr key={inv.session_id} className="border-b border-[var(--peak-border)] hover:bg-[var(--peak-bg)]" data-testid={`invoice-${inv.session_id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{inv.created_at ? new Date(inv.created_at).toLocaleString() : "—"}</td>
                  <td className="px-4 py-3 font-semibold uppercase">{inv.plan}</td>
                  <td className="px-4 py-3">{inv.seats || 1}</td>
                  <td className="px-4 py-3 font-heading font-black">${inv.amount?.toFixed?.(2) ?? inv.amount}</td>
                  <td className={`px-4 py-3 text-[10px] font-bold uppercase tracking-widest ${tone}`}>
                    {paid ? "Paid" : initiated ? "Pending" : "Failed / cancelled"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CurrentPlanCard({ status, plans, onCancel }) {
  const planMeta = plans.find((p) => p.slug === status.plan) || { name: "Free", price: 0 };
  const isPaid = status.plan !== "free";
  const isOverride = status.plan_override_free;
  const isTrialing = status.trial?.in_trial;
  const trialEnded = status.trial?.trial_ended;
  const expires = status.plan_expires_at ? new Date(status.plan_expires_at) : null;

  return (
    <div className={`border-2 p-6 ${isPaid ? "border-[var(--peak-accent)] bg-[var(--peak-ink)] text-white" : "border-[var(--peak-ink)] bg-white"}`} data-testid="current-plan-card">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className={`section-label ${isPaid ? "text-[var(--peak-accent)]" : "text-[var(--peak-muted)]"}`}>
            {isTrialing ? "Trial · Current plan" : "Current plan"}
          </div>
          <div className="font-heading font-black text-4xl md:text-5xl tracking-tighter mt-1 uppercase">{planMeta.name}</div>
          <div className="mt-2 flex items-center gap-2 text-sm">
            {isOverride ? (
              <span className="flex items-center gap-1"><ShieldCheck className="w-4 h-4 text-[var(--peak-accent)]" /> Superadmin comped access</span>
            ) : isTrialing ? (
              <span className="flex items-center gap-1"><Sparkles className="w-4 h-4 text-[var(--peak-accent)]" /> {status.trial.days_left} days left · No card on file</span>
            ) : trialEnded ? (
              <span className="flex items-center gap-1 text-[var(--peak-accent)] font-semibold"><XCircle className="w-4 h-4" /> Trial ended — upgrade to keep Pro features</span>
            ) : isPaid ? (
              <span className="flex items-center gap-1"><CheckCircle2 className="w-4 h-4 text-[var(--peak-accent)]" /> {status.subscription_status === "cancelled" ? "Cancelling at period end" : "Active"}</span>
            ) : (
              <span className="flex items-center gap-1 text-[var(--peak-muted)]">Forever free · 250 contact limit</span>
            )}
          </div>
          {expires && isPaid && (
            <div className="text-xs font-mono mt-2 opacity-80">
              {isTrialing ? "Trial ends" : status.subscription_status === "cancelled" ? "Access ends" : "Renews"} {expires.toLocaleDateString()}
            </div>
          )}
        </div>
        <div className="text-right">
          <div className={`font-heading font-black text-5xl tracking-tighter ${isPaid ? "text-[var(--peak-accent)]" : ""}`}>
            {isTrialing ? "FREE" : `$${planMeta.price ?? 0}`}
            <span className="text-xs font-mono opacity-70 ml-1">/mo</span>
          </div>
          {isPaid && !isTrialing && status.subscription_status !== "cancelled" && (
            <button
              onClick={onCancel}
              data-testid="cancel-subscription-btn"
              className="mt-3 text-xs font-bold uppercase tracking-widest px-3 py-2 border border-white/40 hover:bg-white hover:text-[var(--peak-accent)] transition-colors"
            >
              Cancel subscription
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function PlanCard({ plan, currentSlug, onSelect, busy, seats = 1 }) {
  const isCurrent = plan.slug === currentSlug;
  const isPro = plan.slug === "pro";
  const totalPrice = plan.price * seats;
  return (
    <div
      className={`border-2 p-6 flex flex-col ${isPro ? "border-[var(--peak-accent)] bg-[var(--peak-ink)] text-white" : "border-[var(--peak-ink)] bg-white"}`}
      data-testid={`upgrade-plan-${plan.slug}`}
    >
      <div className="flex items-center justify-between">
        <div className="font-heading font-black text-2xl tracking-tighter">{plan.name}</div>
        {isPro && <span className="text-[10px] font-bold uppercase tracking-widest bg-[var(--peak-accent)] text-white px-2 py-0.5">Popular</span>}
      </div>
      <div className="mt-4 flex items-baseline gap-1">
        <span className="font-heading font-black text-4xl tracking-tighter">${totalPrice}</span>
        <span className={`text-xs font-mono ${isPro ? "text-white/60" : "text-[var(--peak-muted)]"}`}>/mo</span>
      </div>
      {seats > 1 && (
        <div className={`text-[10px] font-mono ${isPro ? "text-white/60" : "text-[var(--peak-muted)]"} mt-1`}>
          ${plan.price} × {seats} seats
        </div>
      )}
      <ul className={`mt-4 space-y-1.5 text-sm flex-1 ${isPro ? "text-white/85" : "text-[var(--peak-ink)]"}`}>
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2">
            <CheckCircle2 className={`w-4 h-4 mt-0.5 shrink-0 ${isPro ? "text-[var(--peak-accent)]" : "text-[var(--peak-ink)]"}`} />
            <span>{f.replace(/_/g, " ")}</span>
          </li>
        ))}
      </ul>
      <button
        data-testid={`checkout-${plan.slug}`}
        disabled={busy || isCurrent}
        onClick={onSelect}
        className={`mt-6 text-xs font-bold uppercase tracking-widest px-4 py-3 flex items-center justify-center gap-2 transition-colors disabled:opacity-40 ${
          isPro
            ? "bg-[var(--peak-accent)] hover:bg-white hover:text-[var(--peak-accent)] text-white"
            : "bg-[var(--peak-ink)] hover:bg-[var(--peak-accent)] text-white"
        }`}
      >
        {isCurrent ? "Current plan" : (<><CreditCard className="w-3.5 h-3.5" /> Upgrade</>)}
      </button>
    </div>
  );
}
